import { QueryClient } from '@tanstack/react-query';

// Enhanced query client with intelligent caching
export const createOptimizedQueryClient = () => {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Stale time: 5 minutes for most data
        staleTime: 5 * 60 * 1000,
        // Cache time: 30 minutes
        gcTime: 30 * 60 * 1000,
        // Retry failed requests
        retry: (failureCount, error: any) => {
          // Don't retry on 4xx errors
          if (error?.status >= 400 && error?.status < 500) {
            return false;
          }
          return failureCount < 3;
        },
        // Background refetch settings
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
        refetchOnMount: true,
      },
      mutations: {
        retry: 1,
      },
    },
  });
};

// Advanced local storage with compression and TTL
export class AdvancedStorage {
  private prefix = 'dialgia_';
  private compressionEnabled = true;

  // Compress large data using simple LZ-string-like compression
  private compress(data: string): string {
    if (!this.compressionEnabled || data.length < 1024) return data;
    
    try {
      // Simple run-length encoding for JSON data
      return data.replace(/(.)\1+/g, (match, char) => {
        return match.length > 3 ? `${char}*${match.length}` : match;
      });
    } catch {
      return data;
    }
  }

  private decompress(data: string): string {
    if (!this.compressionEnabled) return data;
    
    try {
      return data.replace(/(.)\*(\d+)/g, (match, char, count) => {
        return char.repeat(parseInt(count));
      });
    } catch {
      return data;
    }
  }

  setItem(key: string, value: any, ttlMs?: number): void {
    try {
      const item = {
        data: value,
        timestamp: Date.now(),
        ttl: ttlMs,
      };

      const serialized = JSON.stringify(item);
      const compressed = this.compress(serialized);
      localStorage.setItem(this.prefix + key, compressed);
    } catch (error) {
      console.warn('Failed to store item:', key, error);
    }
  }

  getItem<T = any>(key: string): T | null {
    try {
      const stored = localStorage.getItem(this.prefix + key);
      if (!stored) return null;

      const decompressed = this.decompress(stored);
      const item = JSON.parse(decompressed);

      // Check TTL
      if (item.ttl && Date.now() - item.timestamp > item.ttl) {
        this.removeItem(key);
        return null;
      }

      return item.data;
    } catch (error) {
      console.warn('Failed to retrieve item:', key, error);
      return null;
    }
  }

  removeItem(key: string): void {
    localStorage.removeItem(this.prefix + key);
  }

  clear(): void {
    Object.keys(localStorage)
      .filter(key => key.startsWith(this.prefix))
      .forEach(key => localStorage.removeItem(key));
  }

  // Clean up expired items
  cleanup(): void {
    Object.keys(localStorage)
      .filter(key => key.startsWith(this.prefix))
      .forEach(key => {
        const item = this.getItem(key.replace(this.prefix, ''));
        // If getItem returns null, item was expired and removed
      });
  }
}

export const advancedStorage = new AdvancedStorage();

// Service Worker registration moved to serviceWorkerManager
// This function is deprecated and will be removed
export const registerServiceWorker = async (): Promise<ServiceWorkerRegistration | null> => {
  console.warn('⚠️ registerServiceWorker is deprecated. Use serviceWorkerManager.initialize() instead');
  return null;
};

// Preload critical resources
export const preloadCriticalResources = () => {
  const criticalResources = [
    '/favicon.ico',
    // Add critical CSS/JS chunks that are always needed
  ];

  criticalResources.forEach(resource => {
    // Avoid duplicate preloads
    const existing = document.querySelector(`link[rel="preload"][href="${resource}"]`);
    if (existing) return;

    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = resource;

    if (resource.endsWith('.css')) {
      link.as = 'style';
    } else if (resource.endsWith('.js')) {
      link.as = 'script';
    } else if (resource.match(/\.(png|jpg|jpeg|webp|svg)$/)) {
      link.as = 'image';
    } else if (resource.endsWith('.ico')) {
      link.as = 'image';
      link.type = 'image/x-icon';
    }

    document.head.appendChild(link);
  });
};